import React, { useEffect, useState } from 'react';
import config from '../../config.js';
import { fmtRate, asOf } from '../../utils/priceMath';

// Read-only "True-cost labor rates" panel for the Shop Rate Config tab (F08 /
// F44). Reads GET /shop_rates/labor:
//   { as_of, lookback_days, stations: {station: {true_rate, source, punches}}, default_rates, note }
// The endpoint is new; until it is deployed the panel says so instead of
// showing zeros.

export default function QuoteTrueCostRatesPanel() {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${config.API_BASE_URL}/shop_rates/labor`);
        if (!res.ok) {
          const msg = res.status === 404
            ? 'GET /shop_rates/labor is not available on this API yet.'
            : `Could not load true-cost rates (HTTP ${res.status}).`;
          if (!cancelled) setState({ loading: false, error: msg, data: null });
          return;
        }
        const data = await res.json();
        if (!cancelled) setState({ loading: false, error: null, data });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: `Could not load true-cost rates (${e.message}).`, data: null });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const data = state.data || {};
  const stations = data.stations && typeof data.stations === 'object' ? Object.entries(data.stations) : [];
  const defaults = data.default_rates && typeof data.default_rates === 'object' ? data.default_rates : {};

  return (
    <div className="card mb-4 shadow-sm">
      <div className="card-header bg-light fw-bold d-flex justify-content-between align-items-center flex-wrap gap-2">
        <span>True-cost labor rates (wages, {data.lookback_days || 90}-day punch-weighted) <span className="badge bg-secondary ms-1">read-only</span></span>
        {data.as_of && <span className="text-muted small fw-normal">{asOf(data.as_of)}</span>}
      </div>
      <div className="card-body py-2">
        <p className="small text-muted mb-2">
          {data.note || 'True cost labor = punch-weighted employee wage over the last 90 days; falls back to the default wage table when a station has no punches.'}
          {' '}<strong>Machine burden from the Shop Rate Config above is NOT included in cost</strong> — it is a selling-rate input only. The
          quote page's "True $/hr → Cost" column uses these rates; the quote header's Laser/Tap/Brake/Fab/Blast/Finish $/hr are the sell rates.
        </p>
        {state.loading && <div className="text-muted small">Loading…</div>}
        {state.error && <div className="alert alert-secondary py-1 px-2 small mb-0">{state.error}</div>}
        {!state.loading && !state.error && stations.length === 0 && <div className="text-muted small">No stations returned.</div>}
        {stations.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm table-bordered mb-0 align-middle" style={{ fontSize: '0.85rem' }}>
              <thead className="table-light">
                <tr>
                  <th>Station</th>
                  <th className="text-end">True cost $/hr</th>
                  <th>Source</th>
                  <th className="text-end">Punches (90d)</th>
                  <th className="text-end">Default wage $/hr</th>
                </tr>
              </thead>
              <tbody>
                {stations.map(([station, row]) => {
                  const r = row || {};
                  const isDefault = r.source === 'default';
                  return (
                    <tr key={station}>
                      <td className="fw-bold">{station}</td>
                      <td className="text-end">{fmtRate(r.true_rate)}</td>
                      <td>
                        {isDefault
                          ? <span className="badge bg-light text-dark border" title="No punches for this station in the window — default wage table used">default rate</span>
                          : <span className="badge bg-info text-dark" title="Punch-weighted employee wages over the lookback window">{r.source || 'punch_weighted_90d'}</span>}
                      </td>
                      <td className="text-end">{r.punches != null ? r.punches : '—'}</td>
                      <td className="text-end text-muted">{defaults[station] != null ? fmtRate(defaults[station]) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
